import type { OrganizationMembership } from '@/api/auth/missionImpactAuthAPI.schemas';
import { OrganizationMembershipInvitationState } from '@/api/auth/missionImpactAuthAPI.schemas';

type MembershipState = Pick<
  OrganizationMembership,
  'orgId' | 'invitationState' | 'invitationExpiresUtc' | 'membershipExpiresUtc'
>;

/**
 * Returns true when a timestamp is set and is not in the future.
 */
function hasExpired(utc: string | null | undefined, now: Date): boolean {
  return !!utc && new Date(utc).getTime() <= now.getTime();
}

/**
 * Whether the user can select (work in) an organization. Only accepted, unexpired memberships
 * qualify: the Control Plane rejects Invited, Declined and expired memberships with 403.
 */
export function isSelectableMembership(membership: MembershipState, now: Date = new Date()): boolean {
  return (
    membership.invitationState === OrganizationMembershipInvitationState.Accepted &&
    !hasExpired(membership.membershipExpiresUtc, now)
  );
}

/**
 * Whether a membership is an invitation the user can still accept.
 */
export function isPendingInvitation(membership: MembershipState, now: Date = new Date()): boolean {
  return (
    membership.invitationState === OrganizationMembershipInvitationState.Invited &&
    !hasExpired(membership.invitationExpiresUtc, now)
  );
}

/**
 * Picks the organization to select: the cached one when it is still selectable, otherwise the
 * first selectable organization, otherwise null.
 */
export function resolveSelectableOrgId(
  memberships: MembershipState[],
  cachedOrgId: string | null,
  now: Date = new Date()
): string | null {
  const selectable = memberships.filter((m) => isSelectableMembership(m, now));
  if (cachedOrgId && selectable.some((m) => m.orgId === cachedOrgId)) {
    return cachedOrgId;
  }
  return selectable[0]?.orgId ?? null;
}
